import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { engine, engineToken, onEngineUnauthorized } from "./engine-api";

/**
 * Session state for Radar, the engine's staff workspace (founder, 2026-10-02: Drive is
 * extracted and Radar depends on nothing of it).
 *
 * An engine staff account (`auth_users.role` editor, admin or owner): the lock screen asks
 * the engine whether the email is staff, signs in to the engine and keeps its session token
 * in sessionStorage; `/admin/me` says who it is and which permissions it holds. A 401 from
 * the engine clears the token and drops back to the lock screen.
 */
interface Identity {
  id: string;
  email: string;
  role: string;
  displayName: string | null;
}

interface AuthValue {
  user: Identity | null;
  isLoading: boolean;
  /** Staff of the engine: `editor`, `admin` or `owner`. A learner is signed in but must not see the workspace. */
  isEditor: boolean;
  /** Whether the email belongs to a staff account (the lock screen's first step). */
  checkEmail: (email: string) => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** The engine's permission keys for the signed-in staff member. */
  enginePermissions: string[];
}

const AuthContext = createContext<AuthValue | null>(null);

function useEngineAuth(): AuthValue {
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(0);

  useEffect(() => onEngineUnauthorized(() => setVersion((v) => v + 1)), []);

  const token = engineToken.get();
  const me = useQuery({
    queryKey: ["engine", "me", token ?? "", version],
    queryFn: () => engine.admin.me(),
    enabled: !!token,
    retry: false,
  });

  const staff = me.data ?? null;
  const user: Identity | null = staff ? { id: staff.id, email: staff.email, role: staff.role, displayName: staff.name } : null;

  return {
    user,
    isLoading: !!token && me.isPending,
    isEditor: staff !== null,
    enginePermissions: staff?.permissions ?? [],
    async checkEmail(email) {
      return (await engine.staffExists(email.trim())).recognized;
    },
    async signIn(email, password) {
      const r = await engine.signIn(email.trim(), password);
      engineToken.set(r.token);
      setVersion((v) => v + 1);
      await queryClient.invalidateQueries({ queryKey: ["engine"] });
    },
    async signOut() {
      try {
        await engine.signOut();
      } catch (error) {
        // Local state is still cleared: a person who clicked sign out must not be left looking signed in.
        // Logged, not swallowed: a failed round trip means the server session may still be valid.
        console.error("Sign-out request failed; the server session may still be active.", error);
      }
      engineToken.set(null);
      queryClient.clear();
      setVersion((v) => v + 1);
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useEngineAuth();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The engine permissions of whoever is signed in, or none outside the provider (a screen drawn alone in a test). */
export function useEnginePermissions(): readonly string[] {
  return useContext(AuthContext)?.enginePermissions ?? [];
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
